import { createRouter, createWebHistory } from 'vue-router'
import { unifiedRoutes } from './unified-routes'
import { useAuthStore } from '../stores/auth'
import { hrMenu } from '../modules/hr/layout/hrMenuConfig'

const router = createRouter({
  history: createWebHistory('/'),
  routes: unifiedRoutes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  }
})

/**
 * Paths a plain employee may open under /hr. Derived from the same `employee`
 * flags that drive the mega-menu, so the menu and the guard cannot drift apart.
 * Hiding a menu entry is not access control on its own — without this, the
 * hidden screens were still reachable by typing the URL.
 */
const employeeAllowedHrPaths = (() => {
  const paths = new Set<string>(['/hr/dashboard'])
  hrMenu.forEach(group => {
    if (group.employee && group.to) paths.add(group.to)
    group.children?.forEach(child => { if (child.employee) paths.add(child.to) })
  })
  return paths
})()

function isEmployeeAllowed(path: string): boolean {
  // Allow the exact screen and anything nested under it (detail / edit routes).
  return [...employeeAllowedHrPaths].some(
    allowed => path === allowed || path.startsWith(allowed + '/'),
  )
}

function isPrivilegedUser(authStore: ReturnType<typeof useAuthStore>): boolean {
  if (authStore.isAdmin) return true
  try {
    const usersData = JSON.parse(localStorage.getItem('UsersData') || '{}')
    return usersData?.IsAdmin === 1 || usersData?.Admin === 1 || usersData?.IsDeptHead === 1
  } catch {
    return false
  }
}

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta?.requiresAuth)

  if (requiresAuth && !authStore.isAuthenticated) {
    next('/auth/login')
    return
  }

  if (to.path === '/auth/login' && authStore.isAuthenticated) {
    next({ name: 'hrDashboard' })
    return
  }

  // A plain employee is confined to the self-service screens; anything else
  // under /hr sends them back to the dashboard rather than rendering.
  if (
    authStore.isAuthenticated &&
    to.path.startsWith('/hr/') &&
    !isPrivilegedUser(authStore) &&
    !isEmployeeAllowed(to.path)
  ) {
    next('/hr/dashboard')
    return
  }

  next()
})

export default router
